import { useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import DataTable, { Column } from "../../components/DataTable";
import StatTile from "../../components/StatTile";
import CountChartCard from "../../components/CountChartCard";
import Sparkline from "../../components/charts/Sparkline";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchEventsCreated } from "../../dashboardApi";
import { EventCreatorRow, EventsCreatedResponse } from "../../types";
import { card, colors, type, viz } from "../../styles";
import { formatDate, formatNumber, formatRelative, formatUtcDayKey } from "../../format";

/**
 * Who is CREATING things on Markit — the replacement for the "Top event
 * creators" report Markit had in Mixpanel, which is gone for the same free-tier
 * reason every other Mixpanel card here is.
 *
 * **It is not a straight port, and the most important difference is not
 * visual.** The Mixpanel report ranked creators by a count and stopped. A bare
 * count answers "who made the most things" and hides the two questions anyone
 * actually asks next: *are they still doing it*, and *did any of it work*. So
 * every row carries three things the count alone cannot say —
 *
 *  - a **proportional bar**, so the shape of the distribution is readable
 *    without arithmetic (the top creator is usually a multiple of the second),
 *  - a **sparkline of their own creation days**, which separates a creator who
 *    schedules a season in one sitting from one publishing steadily, and
 *  - **recency**, so a creator who stopped six weeks ago cannot sit at the top
 *    of a "top creators" board looking current.
 *
 * **A high count with no attendees is a real and common shape here, and the
 * board shows it rather than filtering it.** The two highest-volume creators in
 * the measured window had zero attendees across everything they made — internal
 * and test-shaped output from accounts that are not on `testAccounts`. Silently
 * dropping them would be inventing an editorial rule; showing attendees beside
 * the count lets a reader see it in one glance.
 *
 * **The attendee column is deliberately mostly empty, and that is honest.** It
 * is only populated for products whose START also fell inside the attendance
 * scan's window — measured at ~55% of the window. The rest were never scanned,
 * so they carry "—" rather than a 0. This is the same "— is not zero" rule the
 * rest of this dashboard runs on, one level deeper.
 */

/** How many creators the board shows before "show all" would be a real feature. */
const TOP_CREATORS = 25;
/** How many rows the recent feed renders. The backend caps its own store at 250. */
const RECENT_SHOWN = 40;

const EventsCreatedView = () => {
  const fetcher = useCallback(() => fetchEventsCreated(), []);
  const { data, error, loading } = useDashboardData<EventsCreatedResponse>(fetcher, []);

  const freshness = { kind: "cached" as const, updatedAt: data?.computedAt ?? undefined };

  const creators = useMemo(() => data?.creators ?? [], [data]);
  const topCreators = useMemo(() => creators.slice(0, TOP_CREATORS), [creators]);
  const maxCreated = topCreators[0]?.created ?? 0;

  // Every day in the window, so a creator's sparkline shows their gaps as gaps
  // rather than closing them up — a row of five bars means five active days,
  // not five consecutive ones. Built from the platform series, which the sync
  // seeds across the whole window for exactly this reason.
  const windowDayKeys = useMemo(
    () => (data?.createdByDay ?? []).map(day => day.date),
    [data]
  );

  const sparkFor = useCallback(
    (row: EventCreatorRow) =>
      windowDayKeys.map(date => ({ date, value: row.byDay?.[date] ?? 0 })),
    [windowDayKeys]
  );

  const columns: Column<EventCreatorRow>[] = [
    {
      key: "creator",
      label: "Creator",
      sortValue: row => (row.name || row.username).toLowerCase(),
      render: row => (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          {row.username ? (
            <a
              href={`https://markitai.com/u/${row.username}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...type.bodyMedium, color: colors.ink, textDecoration: "none" }}
            >
              {row.name || `@${row.username}`}
            </a>
          ) : (
            <span style={{ ...type.bodyMedium, color: colors.inkFaint }} title={row.uid}>
              (account not found)
            </span>
          )}
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            @{row.username || row.uid}
          </span>
        </div>
      ),
    },
    {
      key: "created",
      label: "Created",
      numeric: true,
      sortValue: row => row.created,
      // The number carries the value; the bar carries the distribution. The bar
      // is never the only way to read a figure — the dashboard's standing rule
      // about fills that sit below 3:1 contrast applies to this too.
      render: row => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <span style={{ ...type.bodyMedium, color: colors.ink }}>
            {formatNumber(row.created)}
          </span>
          <div
            style={{
              width: 72,
              height: 4,
              borderRadius: 999,
              background: colors.surfaceMuted,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${maxCreated > 0 ? (row.created / maxCreated) * 100 : 0}%`,
                height: "100%",
                background: viz.series1,
              }}
            />
          </div>
        </div>
      ),
    },
    {
      key: "cadence",
      label: "Cadence",
      // Unsortable on purpose: this column is a shape, not a value, and a sort
      // arrow on it would promise an ordering it cannot deliver.
      render: row => (
        <div
          style={{ width: 110 }}
          title={`${formatNumber(
            Object.keys(row.byDay ?? {}).length
          )} active day(s) in the window`}
        >
          <Sparkline data={sparkFor(row)} dataKey="value" color={viz.series1} height={26} />
        </div>
      ),
    },
    {
      key: "last",
      label: "Last created",
      sortValue: row => row.lastCreatedAt || "",
      render: row => (
        <span title={formatDate(row.lastCreatedAt)} style={{ color: colors.inkMuted }}>
          {formatRelative(row.lastCreatedAt)}
        </span>
      ),
    },
    {
      key: "attendees",
      label: "Attendees",
      numeric: true,
      sortValue: row => row.attendeesScored,
      render: row =>
        row.scoredProducts === 0 ? (
          // Not a zero. None of this creator's products started inside the
          // attendance window, so nothing about their turnout was measured.
          <span
            style={{ color: colors.inkFaint }}
            title="None of this creator's products in this window have started yet, so none were scanned for attendance. This is not zero attendance."
          >
            —
          </span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <span style={{ ...type.bodyMedium, color: colors.ink }}>
              {formatNumber(row.attendeesScored)}
            </span>
            <span
              style={{ ...type.bodySmallRegular, color: colors.inkFaint }}
              title="Attendance is only counted for products whose start date fell inside the nightly scan's window."
            >
              {formatNumber(row.scoredProducts)} of {formatNumber(row.created)} scored
            </span>
          </div>
        ),
    },
  ];

  const scoredShare =
    data && data.productsCreated > 0 ? data.productsScored / data.productsCreated : null;

  return (
    <div>
      {error && <ErrorBanner message={error} />}

      {data && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label="Products created"
              value={formatNumber(data.productsCreated)}
              secondary={
                data.windowFrom
                  ? `Created since ${formatUtcDayKey(data.windowFrom)}`
                  : "Nightly rollup window"
              }
              freshness={freshness}
            />
            <StatTile
              label="Creators"
              value={formatNumber(creators.length)}
              secondary="Made at least one thing in the window"
              freshness={freshness}
            />
            <StatTile
              label="Top creator's share"
              value={
                data.productsCreated > 0 && maxCreated > 0
                  ? `${((maxCreated / data.productsCreated) * 100).toFixed(0)}%`
                  : "—"
              }
              secondary={
                topCreators[0]
                  ? `${formatNumber(maxCreated)} by @${
                      topCreators[0].username || topCreators[0].uid
                    }`
                  : "No creators in the window"
              }
              freshness={freshness}
            />
            <StatTile
              label="Attendance coverage"
              value={scoredShare === null ? "—" : `${(scoredShare * 100).toFixed(0)}%`}
              secondary={`${formatNumber(
                data.productsScored
              )} have started and were scanned`}
              title="Attendance is only known for products whose start date fell inside the nightly scan's window. The rest are scheduled beyond it."
              freshness={freshness}
            />
          </CardGrid>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <CountChartCard
          title="Products created over time"
          subtitle="Events, posts, webinars and products, by their own createdAt"
          seriesLabel="Products created"
          days={data?.createdByDay ?? []}
          color={viz.series1}
          freshness={freshness}
          error={error}
          loading={loading}
          emptyLabel="The nightly rollup hasn't built the creation history yet."
        />
      </div>

      {loading && <LoadingNote />}

      {data && (
        <>
          <div style={{ marginBottom: 8 }}>
            <div style={{ ...type.cardTitle, color: colors.ink }}>
              Top creators by products created
            </div>
            <div style={{ ...type.bodySmallRegular, color: colors.inkFaint, marginTop: 3 }}>
              {creators.length > TOP_CREATORS
                ? `The top ${TOP_CREATORS} of ${formatNumber(creators.length)} creators`
                : `All ${formatNumber(creators.length)} creators`}{" "}
              in the window
            </div>
          </div>

          <DataTable
            rows={topCreators}
            columns={columns}
            rowKey={row => row.uid}
            initialSort={{ key: "created", direction: "desc" }}
            emptyLabel="Nobody created anything in this window."
          />

          <div style={{ marginTop: 16 }}>
            <NoteCard title="This board counts what was CREATED, which is a different window from the Events list">
              The Events tab lists products by when they <strong>start</strong>. This one
              counts them by when they were <strong>made</strong>, and the two populations
              are further apart than they look: over the same 100 days, 761 products start
              in the window and 1,146 were created in it — and{" "}
              <strong>516 of those, about 45%, are scheduled beyond the window</strong> and
              appear on the Events tab not at all. That is why this is its own nightly
              scan rather than a re-count of the rows next door.
              <br />
              <br />
              <strong>Attendees are only known for the overlap.</strong> A product created
              this week for a date in December has not happened, so nothing about its
              turnout was scanned — those rows show &ldquo;—&rdquo;, never a 0. A creator
              with a high count and no attendees is usually one whose output is all still
              ahead of them, or whose products are not the ticketed kind.
              {data.productsScanned > 0 && (
                <>
                  {" "}
                  Last run read {formatNumber(data.productsScanned)} event documents for
                  this board and no ticket subcollections at all — a creation count needs
                  no attendee scan, which is what makes it cheap.
                </>
              )}
            </NoteCard>
          </div>

          <div style={{ marginTop: 24, marginBottom: 8 }}>
            <div style={{ ...type.cardTitle, color: colors.ink }}>Recently created</div>
            {/*
              The feed's own bounds, stated in one line rather than as a
              standing alert card. The rollup stores the newest 250 and this
              renders the newest 40 of those, so with ~1,100 products created in
              a window a truncation warning would fire every single day —
              a permanent alert is one nobody reads, the same reasoning
              `runSync` uses for keeping standing data conditions out of
              `errors`.
            */}
            <div style={{ ...type.bodySmallRegular, color: colors.inkFaint, marginTop: 3 }}>
              Newest first by <code>createdAt</code>, including products scheduled beyond
              the Events tab&rsquo;s window ·{" "}
              {formatNumber(Math.min(data.recent.length, RECENT_SHOWN))} shown of{" "}
              {formatNumber(data.productsCreated)} created
              {data.recentTruncated &&
                ` (the rollup keeps the most recent ${formatNumber(data.recent.length)})`}
            </div>
          </div>

          <div style={{ ...card, overflow: "hidden" }}>
            {data.recent.slice(0, RECENT_SHOWN).map((row, index) => (
              <div
                key={row.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "10px 16px",
                  borderBottom:
                    index === Math.min(data.recent.length, RECENT_SHOWN) - 1
                      ? "none"
                      : `1px solid ${colors.border}`,
                }}
              >
                <div
                  style={{
                    ...type.bodySmallMedium,
                    color: colors.inkFaint,
                    width: 84,
                    flexShrink: 0,
                  }}
                  title={formatDate(row.createdAt)}
                >
                  {formatRelative(row.createdAt)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a
                    href={`https://markitai.com/e/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...type.bodyMedium,
                      color: colors.ink,
                      textDecoration: "none",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row.title}
                  >
                    {row.title || "(untitled)"}
                  </a>
                  <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
                    @{row.creatorUsername || row.createdBy} · {row.eventType || "—"} ·
                    starts {formatDate(row.start)}
                  </span>
                </div>
                <div
                  style={{
                    ...type.bodySmallMedium,
                    color: row.attendees === null ? colors.inkFaint : colors.ink,
                    width: 96,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                  title={
                    row.attendees === null
                      ? "Not yet started, so never scanned for attendance. This is not zero attendance."
                      : `${formatNumber(row.attendees)} claimed, accepted, non-organizer tickets`
                  }
                >
                  {row.attendees === null ? (
                    <span
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <Icon icon="ion:time-outline" height={12} />—
                    </span>
                  ) : (
                    `${formatNumber(row.attendees)} att`
                  )}
                </div>
              </div>
            ))}
            {data.recent.length === 0 && (
              <div
                style={{
                  ...type.bodyRegular,
                  color: colors.inkFaint,
                  padding: "32px 16px",
                  textAlign: "center",
                }}
              >
                The nightly rollup hasn&rsquo;t written the creation feed yet.
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default EventsCreatedView;
